import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../hooks/useAuth'
import { useWindowResizeObserver } from '../../../hooks/useWindowSize'
import { styled } from '../../../lib/stitches'
import { AuthFlowModal } from '../../AuthFlow/AuthFlowModal'
import Button from '../../core/Button/Button'
import {
  ArpeggiLogoColor,
  ArpeggiLogoDark,
  ArrowTopRight,
  DiscordDark,
  DiscordLight,
  InstagramDark,
  InstagramLight,
  MirrorDark,
  MirrorLight,
  TwitterDark,
  TwitterLight
} from '../../core/icons/icons'
import Text from '../../core/Text/Text'
import { PUBLIC_NOTION_URL, SOCIAL_MEDIA_LINKS } from '../constants'
import { scrollToTop } from './Footer'
import Hamburger from './Hamburger'

const segmentMarketingPage = (button: string) => {
  window.analytics.track('Button Clicked', {
    button: button,
    location: 'marketing page',
    source: window.location.pathname
  })
}
const TopbarWrapper = styled('div', {
  position: 'sticky',
  top: 0,
  width: '100%',
  zIndex: 9999,
  height: '72px',
  borderBottom: '1px solid $gray07',
  backgroundRepeat: 'no-repeat',
  '@tablet': {
    border: 'none',
    height: '96px'
  },
  variants: {
    blurredBackground: {
      true: {
        backgroundColor: 'rgba(255, 255, 255, .15)',
        backdropFilter: 'blur(30px)'
      }
    }
  }
})

const TopbarContent = styled('div', {
  maxWidth: '1440px',
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0 16px',
  height: '72px',
  '@mobile': {
    padding: '0 32px'
  },
  '@tablet': {
    padding: '0 110px',
    height: '96px'
  }
})

const ActionWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '> button': {
    marginRight: '16px'
  },
  '@mobile': {
    '> button': {
      marginRight: '32px'
    }
  },
  '@tablet': {
    '> button': {
      marginRight: '40px',
      '&:last-of-type': {
        marginRight: '0px'
      }
    }
  }
})

const Logo = styled('div', {
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  body: {
    display: 'none'
  }
})

const LogoDark = styled('div', {
  display: 'flex',
  svg: {
    height: '26px',
    width: '26px'
  },

  '@tablet': {
    svg: {
      height: '20px',
      width: '20px'
    }
  }
})

const LogoColor = styled('div', {
  display: 'flex',
  alignItems: 'center'
})

const HiddenContent = styled('div', {
  display: 'flex',
  alignItems: 'center'
})

const MobileNav = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  paddingLeft: '32px',
  '> a': {
    marginTop: '32px'
  }
})

const StyledLink = styled(Link, {
  textDecoration: 'none',
  '& span': {
    marginRight: '5px'
  }
})

const ContactUs = styled('a', {
  color: '$gray01',
  fontSize: '24px',
  '&:visited, &:focus, &:active': {
    color: '$gray01'
  }
})

const SocialMedia = styled('div', {
  display: 'none',
  '@tablet': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '> a': {
      marginRight: '20px',
      '&:last-of-type': {
        marginRight: 0
      }
    }
  }
})

const SocialMediaMobile = styled('div', {
  display: 'flex',
  alignItems: 'center',
  '> a': {
    marginRight: '20px'
  },
  marginLeft: '20px',
  marginTop: '40px',

  path: {
    fill: '$gray01'
  },

  '> a:last-child path': {
    fill: 'inherit',
    stroke: '$gray01'
  }
})

const SignUpButton = styled(Button, {
  display: 'none',
  color: '$black',
  fontSize: '16px',
  '@tablet': {
    display: 'block'
  }
})

const REDIRECT_URLS = [
  {
    to: 'mailto:support@arpeggi.io',
    text: 'Contact us'
  },
  {
    to: '/learn',
    text: 'Learn more',
    icon: <ArrowTopRight />
  },
  {
    to: '/home',
    text: 'Sign up'
  }
]

const Topbar = ({ faqRef, hideEnterAppButton }: { faqRef?: any; hideEnterAppButton?: boolean }) => {
  const [topbarBlurredBackground, setTopbarBackgroundBlurred] = useState<boolean>(false)
  const handleScrollEvent = () => {
    const position = window.pageYOffset
    setTopbarBackgroundBlurred(position !== 0)
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScrollEvent)

    return () => {
      window.removeEventListener('scroll', handleScrollEvent)
    }
  }, [])

  const navigate = useNavigate()
  const [isActive, setIsActive] = useState(false)
  const rectangle = useWindowResizeObserver()
  const isMobile = rectangle.width < 960
  const { user, isUserLoading } = useAuth()

  const handleScroll = (e, to) => {
    if (to === '/learn') {
      e.preventDefault()
      e.stopPropagation()
      window.open(PUBLIC_NOTION_URL, '_blank')
    } else if (faqRef && to === '/faq') {
      e.preventDefault()
      e.stopPropagation()
      faqRef?.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  useEffect(() => {
    if (!isMobile) {
      setIsActive(false)
    }
  }, [isMobile])

  const mobileProps = isActive
    ? {
        top: 0,
        height: '100%',
        position: 'fixed',
        backgroundColor: '$black',
        backgroundImage: 'none',
        border: 'none'
      }
    : {}

  return (
    <TopbarWrapper
      blurredBackground={topbarBlurredBackground}
      css={{
        ...mobileProps
      }}
    >
      <TopbarContent>
        <Logo onClick={scrollToTop}>
          <LogoDark
            css={{
              display: isActive ? 'none' : 'flex'
            }}
          >
            <ArpeggiLogoDark />
          </LogoDark>
          <LogoColor
            css={{
              display: !isActive ? 'none' : 'flex'
            }}
          >
            <ArpeggiLogoColor />
          </LogoColor>

          <Text
            size="20"
            css={{
              display: 'none',
              '@tablet': {
                display: 'block',
                marginLeft: '13px',
                fontFamily: '$poppins'
              }
            }}
            type="primary"
          >
            Arpeggi Studio
          </Text>
        </Logo>
        <SocialMedia>
          <a href={SOCIAL_MEDIA_LINKS.TWITTER} target="_blank">
            <TwitterDark />
          </a>
          <a href={SOCIAL_MEDIA_LINKS.INSTAGRAM} target="_blank">
            <InstagramDark />
          </a>
          <a href={SOCIAL_MEDIA_LINKS.DISCORD} target="_blank">
            <DiscordDark />
          </a>
          <a href={SOCIAL_MEDIA_LINKS.MIRROR} target="_blank">
            <MirrorDark />
          </a>
        </SocialMedia>
        <ActionWrapper>
          <AuthFlowModal
            onSuccess={() => {
              navigate('/home')
            }}
            trigger={
              (!user || !user.twitter_handle) &&
              !isUserLoading && (
                <SignUpButton
                  type="secondary"
                  noHover
                  onClick={() => {
                    segmentMarketingPage('Sign Up')
                  }}
                >
                  Sign up
                </SignUpButton>
              )
            }
          />
          <Button
            type="dark"
            onClick={() => {
              segmentMarketingPage('Enter App')
              navigate('/home')
            }}
            css={{
              height: '40px',
              padding: '0 16px',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: 500,
              display: isActive || hideEnterAppButton ? 'none' : 'block'
            }}
          >
            Enter app
          </Button>
          <Hamburger onClick={() => setIsActive((prev) => !prev)} isActive={isActive} />
        </ActionWrapper>
      </TopbarContent>

      <HiddenContent
        css={{
          display: isActive ? 'block' : 'none',
          '@tablet': {
            display: 'none'
          }
        }}
      >
        <MobileNav>
          {REDIRECT_URLS.map((item) => {
            if (item.to[0] === '/') {
              return (
                <StyledLink to={item.to} key={item.text}>
                  <Button
                    onClick={(e) => handleScroll(e, item.to)}
                    css={{
                      color: '$gray01',
                      fontSize: '24px',
                      padding: 0
                    }}
                  >
                    <span>{item.text}</span>
                    {item.icon}
                  </Button>
                </StyledLink>
              )
            }

            return <ContactUs href={item.to}>{item.text}</ContactUs>
          })}
          <Button
            type="white"
            onClick={() => navigate('/home')}
            css={{
              height: '46px',
              padding: '0 37px',
              borderRadius: '16px',
              fontSize: '18px',
              fontWeight: 500,
              marginTop: '35px',
              display: hideEnterAppButton ? 'none' : 'block'
            }}
          >
            Enter app
          </Button>
          <SocialMediaMobile>
            <a href={SOCIAL_MEDIA_LINKS.TWITTER} target="_blank">
              <TwitterLight />
            </a>
            <a href={SOCIAL_MEDIA_LINKS.INSTAGRAM} target="_blank">
              <InstagramLight />
            </a>
            <a href={SOCIAL_MEDIA_LINKS.DISCORD} target="_blank">
              <DiscordLight />
            </a>
            <a href={SOCIAL_MEDIA_LINKS.MIRROR} target="_blank">
              <MirrorLight />
            </a>
          </SocialMediaMobile>
        </MobileNav>
      </HiddenContent>
    </TopbarWrapper>
  )
}

export default observer(Topbar)
